'use client';

import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Toaster.module.css';
import { log } from '@/lib/log';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  title?: string;
  description?: string;
  type?: ToastType;
  duration?: number;
}

interface Toast extends ToastOptions {
  id: string;
  createdAt: number;
}

interface ToasterContextValue {
  create: (options: ToastOptions) => void;
}

const ToasterContext = createContext<ToasterContextValue>({} as ToasterContextValue);

export function ToasterProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  
  // Use useEffect to handle client-side mounting
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  const create = useCallback((options: ToastOptions) => {
    const { title, description, type = 'info', duration = 5000 } = options;
    
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = {
      id,
      title,
      description,
      type,
      duration,
      createdAt: Date.now(),
    };
    
    setToasts((prevToasts) => [...prevToasts, newToast]);
    
    if (duration !== Infinity) {
      setTimeout(() => {
        setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
      }, duration);
    }
  }, []);
  
  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);
  
  return (
    <ToasterContext.Provider value={{ create }}>
      {children}
      {isMounted && createPortal(
        <div className={styles['toaster-container']} suppressHydrationWarning>
          {toasts.map((toast) => (
            <ToastComponent 
              key={toast.id} 
              toast={toast} 
              onClose={() => removeToast(toast.id)} 
            />
          ))}
        </div>,
        document.body
      )}
    </ToasterContext.Provider>
  );
}

interface ToastComponentProps {
  toast: Toast;
  onClose: () => void;
}

function ToastComponent({ toast, onClose }: ToastComponentProps) {
  const { title, description, type, duration = 5000, createdAt } = toast;
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (duration === Infinity) return;
    
    const startTime = Date.now();
    const endTime = createdAt + duration;
    const remainingTime = endTime - startTime;
    
    if (remainingTime <= 0) {
      onClose();
      return;
    }
    
    const updateProgress = () => {
      const now = Date.now();
      const remaining = endTime - now;
      const newProgress = (remaining / duration) * 100;
      
      if (newProgress <= 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        onClose();
      } else {
        setProgress(newProgress);
      }
    };
    
    intervalRef.current = setInterval(updateProgress, 10);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [createdAt, duration, onClose]);
  
  return (
    <div className={`${styles.toast} ${styles[`toast-${type}`]}`} suppressHydrationWarning>
      {title && <div className={styles['toast-title']} suppressHydrationWarning>{title}</div>}
      {description && <div className={styles['toast-description']} suppressHydrationWarning>{description}</div>}
      <button className={styles['toast-close']} onClick={onClose} aria-label="Close toast" suppressHydrationWarning>
        ✕
      </button>
      {duration !== Infinity && (
        <div 
          className={styles['toast-progress']} 
          style={{ width: `${progress}%` }}
          suppressHydrationWarning
        />
      )}
    </div>
  );
}

export function useToaster() {
  const context = useContext(ToasterContext);
  if (context === undefined) {
    throw new Error('useToaster must be used within a ToasterProvider');
  }
  return context;
}

export const toaster = {
  create: (options: ToastOptions) => {
    void options;
    // This is a noop by default, but will be overridden by the actual implementation
    // when the ToasterProvider is mounted.
    log.warn(
      'Toaster was called before it was initialized. Make sure you have the ToasterProvider mounted.'
    );
  },
};

// This is a hack to allow the toaster to be used outside of React components
export function Toaster() {
  const { create } = useToaster();

  useEffect(() => {
    toaster.create = create;
    return () => {
      toaster.create = (options: ToastOptions) => {
        void options;
        log.warn(
          'Toaster was called after it was unmounted. Make sure you have the ToasterProvider mounted.'
        );
      };
    };
  }, [create]);

  return null;
}
